import React, { useState, useMemo } from 'react';
import { BsGrid3X3GapFill, BsList, BsGrid, BsSearch, BsFilter } from 'react-icons/bs';
import OfficeCard from './index.jsx';
import './index.css';
import { getUser, formatCurrency } from '../../../utils/Common.js';
import Spinner from '../../Spinner';

const OfficeCardContainer = ({ 
    offices, 
    isLoading, 
    handleTransaction, 
    handleReloadOfficeStatus, 
    handleNewBalanceChange,
    newBalance,
    pendingRequests = {}
}) => {
    const user = getUser();
    const [viewType, setViewType] = useState('line'); 
    const [filters, setFilters] = useState({
        estafeta: '',
        codigo: '',
        provincia: 'todos'
    });

    // Filtrar estafetas basado en los filtros
    const filteredOffices = useMemo(() => {
        // Para usuarios con roles 2 y 3, no aplicar filtros (solo ven 1 sucursal)
        if (user?.roleId <= 3) {
            return offices;
        }

        return offices.filter(office => {
            const matchEstafeta = office.name?.toLowerCase().includes(filters.estafeta.toLowerCase());
            const matchCodigo = office.code?.toLowerCase().includes(filters.codigo.toLowerCase());
            
            let matchProvincia = true;
            if (filters.provincia !== 'todos') {
                matchProvincia = office.nombreProvincia === filters.provincia;
            }

            return matchEstafeta && matchCodigo && matchProvincia;
        });
    }, [offices, filters, user?.roleId]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            estafeta: '',
            codigo: '',
            provincia: 'todos'
        });
    };

    // Obtener lista única de provincias
    const provincias = useMemo(() => {
        const uniqueProvincias = [...new Set(offices.map(office => office.nombreProvincia).filter(Boolean))];
        return uniqueProvincias.sort();
    }, [offices]);

    const renderViewControls = () => {
        // Ocultar controles para usuarios con roles 2 y 3 (solo ven 1 sucursal)
        if (user?.roleId <= 3) {
            return null;
        }

        return (
            <div className="dashboardCard view-controls mb-3">
                <div className="row align-items-center">
                    {/* Filtros - Izquierda */}
                    <div className="col-md-8">
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                            <div className="d-flex align-items-center gap-2 mr-2">
                                <BsFilter size={14} className="text-muted" />
                                <span className="text-muted small">Filtros:</span>
                            </div>
                            
                            {/* Filtro por Estafeta */}
                            <div className="d-flex align-items-center gap-1">
                                <label className="small text-muted mb-0 mr-2">Estafeta:</label>
                                <div style={{
                                    position: 'relative',
                                    width: '150px',
                                    borderRadius: '6px',
                                    boxShadow: '0 2px 8px rgba(15,67,114,0.08)',
                                    background: 'linear-gradient(90deg, #f8fafc 0%, #e9ecef 100%)',
                                    border: '1px solid #0F4372',
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '2px 8px'
                                }}>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        style={{
                                            width: '100%',
                                            border: 'none',
                                            background: 'transparent',
                                            boxShadow: 'none',
                                            outline: 'none',
                                            fontWeight: 500,
                                            color: '#0F4372',
                                            paddingLeft: 0
                                        }}
                                        placeholder="Buscar estafeta..."
                                        value={filters.estafeta}
                                        onChange={(e) => handleFilterChange('estafeta', e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Filtro por Código */}
                            <div className="d-flex align-items-center gap-1">
                                <label className="small text-muted mb-0 mr-2">Código:</label>
                                <div style={{
                                    position: 'relative',
                                    width: '130px',
                                    borderRadius: '6px',
                                    boxShadow: '0 2px 8px rgba(15,67,114,0.08)',
                                    background: 'linear-gradient(90deg, #f8fafc 0%, #e9ecef 100%)',
                                    border: '1px solid #0F4372',
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '2px 8px'
                                }}>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        style={{
                                            width: '100%',
                                            border: 'none',
                                            background: 'transparent',
                                            boxShadow: 'none',
                                            outline: 'none',
                                            fontWeight: 500,
                                            color: '#0F4372',
                                            paddingLeft: 0
                                        }}
                                        placeholder="Buscar código..."
                                        value={filters.codigo}
                                        onChange={(e) => handleFilterChange('codigo', e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Filtro por Provincia */}
                            <div className="d-flex align-items-center gap-1">
                                <label className="small text-muted mb-0 mr-3">Provincia:</label>
                                <div style={{
                                    position: 'relative',
                                    width: '120px',
                                    borderRadius: '6px',
                                    boxShadow: '0 2px 8px rgba(15,67,114,0.08)',
                                    background: 'linear-gradient(90deg, #f8fafc 0%, #e9ecef 100%)',
                                    border: '1px solid #0F4372',
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '2px 8px'
                                }}>
                                    <select
                                        className="form-control form-control-sm"
                                        style={{
                                            width: '100%',
                                            border: 'none',
                                            background: 'transparent',
                                            boxShadow: 'none',
                                            outline: 'none',
                                            fontWeight: 500,
                                            color: '#0F4372',
                                            paddingLeft: 0
                                        }}
                                        value={filters.provincia}
                                        onChange={(e) => handleFilterChange('provincia', e.target.value)}
                                    >
                                        <option value="todos">Todas</option>
                                        {provincias.map(provincia => (
                                            <option key={provincia} value={provincia}>
                                                {provincia}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {(filters.estafeta || filters.codigo || filters.provincia !== 'todos') && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={clearFilters}
                                    title="Limpiar filtros"
                                >
                                    Limpiar
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="col-md-4">
                        <div className="d-flex align-items-center justify-content-end gap-2">
                            <span className="text-muted mr-2">Vista:</span>
                            <div className="btn-group" role="group">
                                <button
                                    type="button"
                                    className={`btn btn-sm ${viewType === 'line' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setViewType('line')}
                                    title="Vista línea"
                                >
                                    <BsList size={16} />
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm ${viewType === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setViewType('grid')}
                                    title="Vista grid"
                                >
                                    <BsGrid3X3GapFill size={16} />
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm ${viewType === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setViewType('table')}
                                    title="Vista tabla"
                                >
                                    <BsGrid size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row mt-2">
                    <div className="col-12">
                        <div className="d-flex align-items-center gap-2">
                            <BsSearch size={12} className="text-muted mr-2" />
                            <span className="small text-muted">
                                Mostrando {filteredOffices.length} de {offices.length} estafetas
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderOffices = () => {
        // Para usuarios con roles 2 y 3, siempre usar vista línea
        const effectiveViewType = user?.roleId <= 3 ? 'line' : viewType;

        if (effectiveViewType === 'table') {
            return (
                <div className="dashboardCard office-table-container">
                    <div className="office-table-header">
                        <div>Estafeta</div>
                        <div>Código</div>
                        <div>Provincia</div>
                        <div>Saldo</div>
                        <div>Acciones</div>
                    </div>
                    {filteredOffices.map((office, index) => (
                        <OfficeCard
                            key={index}
                            office={office}
                            handleTransaction={handleTransaction}
                            handleReloadOfficeStatus={handleReloadOfficeStatus}
                            newBalance={newBalance}
                            handleNewBalanceChange={handleNewBalanceChange}
                            isLoading={isLoading}
                            hasPendingRequest={pendingRequests[office.id]}
                            viewType={effectiveViewType}
                        />
                    ))}
                </div>
            );
        }

        if (effectiveViewType === 'grid') {
            return (
                <div className="office-grid-container">
                    {filteredOffices.map((office, index) => (
                        <OfficeCard
                            key={index}
                            office={office}
                            handleTransaction={handleTransaction}
                            handleReloadOfficeStatus={handleReloadOfficeStatus}
                            newBalance={newBalance}
                            handleNewBalanceChange={handleNewBalanceChange}
                            isLoading={isLoading}
                            hasPendingRequest={pendingRequests[office.id]}
                            viewType={effectiveViewType}
                        />
                    ))}
                </div>
            );
        }

        // Vista línea (por defecto)
        return (
            <div className="office-line-container">
                {filteredOffices.map((office, index) => (
                    <OfficeCard
                        key={index}
                        office={office}
                        handleTransaction={handleTransaction}
                        handleReloadOfficeStatus={handleReloadOfficeStatus}
                        newBalance={newBalance}
                        handleNewBalanceChange={handleNewBalanceChange}
                        isLoading={isLoading}
                        hasPendingRequest={pendingRequests[office.id]}
                        viewType={effectiveViewType}
                    />
                ))}
            </div>
        );
    };

    return (
        <div className="office-card-container">
            {renderViewControls()}
            {renderOffices()}
        </div>
    );
};

export default OfficeCardContainer;
